import React, { useMemo, useState } from 'react';
import {
  DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ChevronLeft, ChevronRight, GripVertical, Clock, MapPin, Wrench,
} from 'lucide-react';
import { facilityAPI } from '../../lib/api';
import { URGENCY_STYLES, URGENCY_LABELS } from './constants';
import { toast } from 'sonner';

const startOfWeek = (d) => {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Mo=0
  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);
  return x;
};
const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const addMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() + n); return x; };
const isoDay = (d) => {
  const x = new Date(d); x.setHours(12, 0, 0, 0);
  return x.toISOString().slice(0, 10);
};
const DAY_LABELS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const timeStr = (iso) => new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

// Liste aller ISO-Tage von start bis end (inkl.), gedeckelt auf 60 Tage.
const daysInRange = (startIso, endIso) => {
  const out = [];
  const s = new Date(startIso); s.setHours(12, 0, 0, 0);
  const e = endIso ? new Date(endIso) : new Date(startIso); e.setHours(12, 0, 0, 0);
  let cur = new Date(s);
  let guard = 0;
  while (cur <= e && guard < 60) { out.push(isoDay(cur)); cur.setDate(cur.getDate() + 1); guard += 1; }
  return out.length ? out : [isoDay(s)];
};

// Kompakte Karte (Monat) / breitere Karte (Woche via `wide`)
const TaskCard = ({ ticket, onClick, dragProps, wide }) => (
  <div
    className={`rounded-lg border border-border/60 bg-background shadow-sm hover:shadow transition-shadow cursor-pointer ${wide ? 'p-2.5' : 'p-1.5'} ${ticket._multiday ? 'border-l-2 border-l-primary' : ''}`}
    onClick={onClick}
    data-testid={`facility-cal-card-${ticket.id}`}
  >
    <div className="flex items-start gap-1.5">
      {dragProps && (
        <span {...dragProps.listeners} {...dragProps.attributes} className="cursor-grab touch-none pt-0.5" onClick={(e) => e.stopPropagation()}>
          <GripVertical className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        </span>
      )}
      <div className="min-w-0 flex-1">
        <p className={`font-medium truncate ${wide ? 'text-sm' : 'text-xs'}`}>
          {ticket._multiday && <span className="text-primary mr-0.5" title="Mehrtägig">{ticket._spanStart ? '▶' : ticket._spanEnd ? '◀' : '↔'}</span>}
          {ticket.title}
        </p>
        <div className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-muted-foreground ${wide ? 'text-xs' : 'text-[10px]'}`}>
          {ticket._multiday ? (
            <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{isoDayDe(ticket.scheduled_start)}–{isoDayDe(ticket.scheduled_end)}</span>
          ) : (
            ticket.scheduled_start && <span className="inline-flex items-center gap-0.5"><Clock className="h-3 w-3" />{timeStr(ticket.scheduled_start)}</span>
          )}
          {ticket.location_name && <span className="inline-flex items-center gap-0.5 truncate"><MapPin className="h-3 w-3" />{ticket.location_name}</span>}
          {wide && ticket.assigned_names?.length > 0 && <span className="inline-flex items-center gap-0.5 truncate"><Wrench className="h-3 w-3" />{ticket.assigned_names.join(', ')}</span>}
        </div>
      </div>
      <span className={`shrink-0 rounded-full ${URGENCY_STYLES[ticket.urgency]} ${wide ? 'text-[10px] px-1.5' : 'w-2 h-2 mt-1'}`} title={URGENCY_LABELS[ticket.urgency]}>
        {wide ? URGENCY_LABELS[ticket.urgency] : ''}
      </span>
    </div>
  </div>
);

const isoDayDe = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');

const DraggableCard = ({ ticket, onClick, wide, dragId }) => {
  const { setNodeRef, listeners, attributes, transform, isDragging } = useDraggable({ id: dragId || ticket.id });
  const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)`, opacity: isDragging ? 0.5 : 1, zIndex: 50 } : undefined;
  return (
    <div ref={setNodeRef} style={style}>
      <TaskCard ticket={ticket} onClick={onClick} dragProps={{ listeners, attributes }} wide={wide} />
    </div>
  );
};

// Woche: breite Tageszeile (volle Breite)
const WeekRow = ({ day, tickets, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `day-${isoDay(day)}` });
  const today = isoDay(new Date()) === isoDay(day);
  const weekend = [5, 6].includes((day.getDay() + 6) % 7);
  return (
    <div className={`flex gap-3 rounded-xl border p-2 transition-colors ${isOver ? 'bg-primary/10 border-primary' : 'border-border/50'} ${today ? 'ring-1 ring-primary/40' : ''} ${weekend ? 'bg-secondary/20' : ''}`}>
      <div className={`w-24 shrink-0 flex flex-col items-center justify-center rounded-lg py-2 ${today ? 'bg-primary/10 text-primary' : 'text-muted-foreground'}`}>
        <span className="text-xs font-medium">{DAY_LABELS[(day.getDay() + 6) % 7]}</span>
        <span className="text-lg font-heading font-semibold leading-none">{day.getDate()}</span>
        <span className="text-[10px]">{MONTHS[day.getMonth()].slice(0, 3)}</span>
      </div>
      <div ref={setNodeRef} className="flex-1 min-w-0 flex flex-wrap gap-2 items-start min-h-[48px] content-start py-1" data-testid={`facility-day-${isoDay(day)}`}>
        {tickets.length === 0
          ? <span className="text-xs text-muted-foreground/60 self-center">–</span>
          : tickets.map((t) => <div key={`${t.id}-${t._dayKey}`} className="w-full sm:w-[calc(50%-4px)] lg:w-[calc(33.333%-6px)]"><DraggableCard ticket={t} dragId={`${t.id}__${t._dayKey}`} onClick={() => onClick(t)} wide /></div>)}
      </div>
    </div>
  );
};

// Monat: Rasterzelle
const MonthCell = ({ day, inMonth, tickets, onClick }) => {
  const { setNodeRef, isOver } = useDroppable({ id: `day-${isoDay(day)}` });
  const today = isoDay(new Date()) === isoDay(day);
  return (
    <div
      ref={setNodeRef}
      className={`min-h-[92px] rounded-lg border p-1 space-y-1 transition-colors ${isOver ? 'bg-primary/10 border-primary' : 'border-border/40'} ${inMonth ? '' : 'opacity-40 bg-secondary/20'} ${today ? 'ring-1 ring-primary/50' : ''}`}
      data-testid={`facility-day-${isoDay(day)}`}
    >
      <div className={`text-[11px] font-medium text-right pr-0.5 ${today ? 'text-primary' : 'text-muted-foreground'}`}>{day.getDate()}</div>
      {tickets.slice(0, 4).map((t) => <DraggableCard key={`${t.id}-${t._dayKey}`} ticket={t} dragId={`${t.id}__${t._dayKey}`} onClick={() => onClick(t)} />)}
      {tickets.length > 4 && <div className="text-[10px] text-muted-foreground text-center">+{tickets.length - 4} weitere</div>}
    </div>
  );
};

export const FacilityCalendar = ({ tickets, onOpenTicket, onChanged }) => {
  const [mode, setMode] = useState('week'); // 'week' | 'month' | 'agenda'
  const [anchor, setAnchor] = useState(new Date());
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const { backlog, byDay } = useMemo(() => {
    const relevant = tickets.filter((t) => ['approved', 'in_progress', 'quote_requested'].includes(t.status));
    const bl = relevant.filter((t) => !t.scheduled_start);
    const map = {};
    relevant.filter((t) => t.scheduled_start).forEach((t) => {
      const days = daysInRange(t.scheduled_start, t.scheduled_end);
      const multiday = days.length > 1;
      days.forEach((k, idx) => {
        const copy = { ...t, _dayKey: k, _multiday: multiday, _spanStart: idx === 0, _spanEnd: idx === days.length - 1 };
        (map[k] = map[k] || []).push(copy);
      });
    });
    Object.values(map).forEach((arr) => arr.sort((a, b) => (a.scheduled_start || '').localeCompare(b.scheduled_start || '')));
    return { backlog: bl, byDay: map };
  }, [tickets]);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(startOfWeek(anchor), i)), [anchor]);
  const monthCells = useMemo(() => {
    const first = startOfMonth(anchor);
    const gridStart = startOfWeek(first);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [anchor]);

  // Agenda: nach Beginn-Tag gruppiert (mehrtägige Aufgaben nur einmal, mit Zeitraum-Hinweis)
  const agendaGroups = useMemo(() => {
    const scheduled = tickets.filter((t) => ['approved', 'in_progress', 'quote_requested'].includes(t.status) && t.scheduled_start);
    const groups = {};
    scheduled.forEach((t) => { const k = isoDay(new Date(t.scheduled_start)); (groups[k] = groups[k] || []).push(t); });
    const todayKey = isoDay(new Date());
    return Object.keys(groups).sort().map((k) => {
      const d = new Date(`${k}T12:00:00`);
      let label = `${DAY_LABELS[(d.getDay() + 6) % 7]}, ${d.getDate()}. ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
      if (k === todayKey) label = `Heute · ${label}`;
      else if (k === isoDay(addDays(new Date(), 1))) label = `Morgen · ${label}`;
      const arr = groups[k].slice().sort((a, b) => (a.scheduled_start || '').localeCompare(b.scheduled_start || ''));
      return { key: k, label, overdue: k < todayKey, tickets: arr };
    });
  }, [tickets]);

  const onDragEnd = async (e) => {
    const overId = e.over?.id;
    const rawId = e.active?.id;
    if (!overId || !rawId || !String(overId).startsWith('day-')) return;
    const date = String(overId).replace('day-', '');
    const ticketId = String(rawId).split('__')[0];
    const ticket = tickets.find((t) => t.id === ticketId);
    if (ticket && ticket.scheduled_start && isoDay(new Date(ticket.scheduled_start)) === date) return;
    const time = ticket?.scheduled_start ? new Date(ticket.scheduled_start).toTimeString().slice(0, 5) : '09:00';
    const newStart = new Date(`${date}T${time}:00`);
    let newEnd = null;
    if (ticket?.scheduled_end && ticket?.scheduled_start) {
      const durMs = new Date(ticket.scheduled_end).getTime() - new Date(ticket.scheduled_start).getTime();
      if (durMs > 0) newEnd = new Date(newStart.getTime() + durMs).toISOString();
    }
    try {
      await facilityAPI.schedule(ticketId, { scheduled_start: newStart.toISOString(), scheduled_end: newEnd });
      toast.success('Termin gesetzt');
      onChanged?.();
    } catch {
      toast.error('Termin konnte nicht gesetzt werden');
    }
  };

  const goPrev = () => setAnchor(mode === 'week' ? addDays(anchor, -7) : addMonths(anchor, -1));
  const goNext = () => setAnchor(mode === 'week' ? addDays(anchor, 7) : addMonths(anchor, 1));
  const rangeLabel = mode === 'week'
    ? `${weekDays[0].toLocaleDateString('de-DE')} – ${weekDays[6].toLocaleDateString('de-DE')}`
    : `${MONTHS[anchor.getMonth()]} ${anchor.getFullYear()}`;

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Backlog */}
        <div className="lg:w-64 shrink-0 rounded-xl border border-border/60 p-3 space-y-2" data-testid="facility-backlog">
          <p className="text-sm font-medium">Nicht terminiert ({backlog.length})</p>
          <p className="text-xs text-muted-foreground">Auf einen Tag ziehen, um zu terminieren.</p>
          <div className="space-y-1.5 max-h-[65vh] overflow-y-auto pr-1">
            {backlog.length === 0
              ? <p className="text-xs text-muted-foreground py-4 text-center">Alles terminiert 🎉</p>
              : backlog.map((t) => <DraggableCard key={t.id} ticket={t} onClick={() => onOpenTicket(t)} />)}
          </div>
        </div>

        {/* Kalender */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
            <div className="inline-flex rounded-full border border-border/60 p-0.5 bg-secondary/30" data-testid="facility-cal-mode">
              <button className={`px-3 py-1 text-sm rounded-full transition-colors ${mode === 'week' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`} onClick={() => setMode('week')} data-testid="facility-mode-week">Woche</button>
              <button className={`px-3 py-1 text-sm rounded-full transition-colors ${mode === 'month' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`} onClick={() => setMode('month')} data-testid="facility-mode-month">Monat</button>
              <button className={`px-3 py-1 text-sm rounded-full transition-colors ${mode === 'agenda' ? 'bg-background shadow-sm font-medium' : 'text-muted-foreground'}`} onClick={() => setMode('agenda')} data-testid="facility-mode-agenda">Liste</button>
            </div>
            {mode !== 'agenda' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="rounded-full" onClick={goPrev} data-testid="facility-cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
                <span className="text-sm font-medium min-w-[180px] text-center">{rangeLabel}</span>
                <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setAnchor(new Date())} data-testid="facility-cal-today">Heute</Button>
                <Button variant="outline" size="sm" className="rounded-full" onClick={goNext} data-testid="facility-cal-next"><ChevronRight className="h-4 w-4" /></Button>
              </div>
            )}
          </div>

          {mode === 'week' ? (
            <div className="space-y-2">
              {weekDays.map((d) => <WeekRow key={isoDay(d)} day={d} tickets={byDay[isoDay(d)] || []} onClick={onOpenTicket} />)}
            </div>
          ) : mode === 'month' ? (
            <div>
              <div className="grid grid-cols-7 gap-1 mb-1">
                {DAY_LABELS.map((l) => <div key={l} className="text-center text-xs font-medium text-muted-foreground">{l}</div>)}
              </div>
              <div className="grid grid-cols-7 gap-1">
                {monthCells.map((d) => (
                  <MonthCell key={isoDay(d)} day={d} inMonth={d.getMonth() === anchor.getMonth()} tickets={byDay[isoDay(d)] || []} onClick={onOpenTicket} />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4" data-testid="facility-agenda">
              {agendaGroups.length === 0 ? (
                <p className="text-sm text-muted-foreground py-10 text-center">Keine terminierten Aufgaben. Ziehe Aufgaben aus dem Backlog auf einen Tag (Woche/Monat) oder setze im Detail einen Termin.</p>
              ) : agendaGroups.map((g) => (
                <div key={g.key} data-testid={`facility-agenda-group-${g.key}`}>
                  <div className="flex items-center gap-2 mb-1.5">
                    <h3 className={`text-sm font-heading font-semibold ${g.overdue ? 'text-destructive' : ''}`}>{g.label}</h3>
                    {g.overdue && <Badge variant="outline" className="text-[10px] text-destructive border-destructive/40">überfällig</Badge>}
                    <span className="text-xs text-muted-foreground">{g.tickets.length} Aufgabe(n)</span>
                  </div>
                  <div className="space-y-1.5">
                    {g.tickets.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => onOpenTicket(t)}
                        className="w-full text-left rounded-lg border border-border/60 bg-background px-3 py-2.5 hover:shadow-sm hover:border-primary/40 transition-all flex items-center gap-3"
                        data-testid={`facility-agenda-item-${t.id}`}
                      >
                        <span className="shrink-0 w-14 text-sm font-medium tabular-nums">{t.scheduled_start ? timeStr(t.scheduled_start) : '—'}</span>
                        <span className="min-w-0 flex-1">
                          <span className="font-medium text-sm block truncate">{t.title}</span>
                          <span className="text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                            {t.scheduled_end && isoDayDe(t.scheduled_end) !== isoDayDe(t.scheduled_start) && (
                              <span className="inline-flex items-center gap-1 text-primary">↔ bis {isoDayDe(t.scheduled_end)}</span>
                            )}
                            {t.location_name && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{t.location_name}{t.room_name ? ` · ${t.room_name}` : ''}</span>}
                            {t.assigned_names?.length > 0 && <span className="inline-flex items-center gap-1"><Wrench className="h-3 w-3" />{t.assigned_names.join(', ')}</span>}
                          </span>
                        </span>
                        <Badge variant="outline" className={`shrink-0 text-[10px] ${URGENCY_STYLES[t.urgency]}`}>{URGENCY_LABELS[t.urgency]}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DndContext>
  );
};
